import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import gql from "graphql-tag";
import {
  Title,
  Fieldset,
  Button,
  Field,
  Control,
  Label,
  Input,
  Radio,
  Column
} from "rbx";
import { useMutation, useQuery } from "react-apollo";
import { useCurrentUser } from "../../utils/hooks";
import { DateInput, DateTime } from "../inputs";
import { getDaysDiffBetweenDates, round, ourToast as toast } from "../../utils";
import { UPDATE_ORDER_MUTATION } from "../../graphql/Orders/Orders.graphql";
import Loading from "../Loading";

const ALL_SYSTEM_CODES_QUERY = gql`
  query SystemCodes($where: SystemCodeWhereInput!) {
    SystemCodes(where: $where) {
      RecId
      Type
      Category
      CodeName
      CodeId
      CodeDescription
    }
  }
`;

const VoidOrderForm = ({ inputs, OrderRecIdRef, setModalOpen }) => {
  const SystemCodes = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category_in: ["Order"],
        CodeName_in: ["OrderStatus"],
        CodeId_in: ["CAN", "VD"]
      }
    }
  });
  const [voidData, setVoidData] = useState({ ...inputs });
  const [notice, setNotice] = useState("");
  const { data: currentUser } = useCurrentUser();
  const [States, setStates] = useState(null);
  const [readOnly, setReadOnly] = useState(false);

  const handleChange = e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const updates = { ...voidData };

    setVoidData({ ...updates, [e.target.name]: value });
  };

  const [voidOrder, { loading }] = useMutation(UPDATE_ORDER_MUTATION);
  const [type, setType] = useState(null);

  const handleToggleType = value => () => {
    setVoidData(prev => ({
      ...prev,
      StatusRecIdRef:
        value === "void" ? States.Voided.RecId : States.Cancelled.RecId
    }));
  };

  useEffect(() => {
    if (voidData.ScheduledDeliveryDate && voidData.VoidNotificationDate) {
      const daysDiff = getDaysDiffBetweenDates(
        new Date(voidData.VoidNotificationDate),
        new Date(voidData.ScheduledDeliveryDate)
      );
      const hours = daysDiff * 24;
      setNotice(hours);
    }
    if (SystemCodes?.data?.SystemCodes?.length > 0 && !States && !type) {
      const Cancelled = SystemCodes.data.SystemCodes.find(
        s => s.CodeId === "CAN"
      );
      const Voided = SystemCodes.data.SystemCodes.find(s => s.CodeId === "VD");
      setStates({
        Cancelled,
        Voided
      });
      setType(
        parseInt(voidData.StatusRecIdRef === Voided.RecId) ? "Void" : "Cancel"
      );
      setReadOnly(
        [Cancelled.RecId, Voided.RecId].includes(inputs.StatusRecIdRef)
      );
    }
  }, [
    States,
    SystemCodes,
    inputs.StatusRecIdRef,
    voidData.ScheduledDeliveryDate,
    voidData.VoidNotificationDate,
    type,
    voidData.StatusRecIdRef
  ]);
  if (SystemCodes?.loading || !States?.Voided || !States?.Cancelled) {
    return <Loading />;
  }

  return (
    <form
      id="void-order-form"
      method="POST"
      style={{ width: 600, maxWidth: "100%" }}
      onSubmit={async e => {
        e.preventDefault();
        try {
          await voidOrder({
            variables: {
              data: {
                VoidDate: voidData.VoidDate
                  ? new Date(voidData.VoidDate)
                  : null,
                VoidReason: voidData.VoidReason,
                VoidNotificationContact: voidData.VoidNotificationContact,
                VoidNotificationDate: voidData.VoidNotificationDate
                  ? new Date(voidData.VoidNotificationDate)
                  : null,
                VoidNotificationBy:
                  voidData.VoidNotificationBy || currentUser?.UserId,
                StatusRecIdRef: parseInt(voidData.StatusRecIdRef),
                Trip: null,
                Leg: null,
                BayNumber: null,
                PickupDriverRecIdRef: null,
                DeliveryDriverRecIdRef: null,
                PickupTractorRecIdRef: null,
                DeliveryTractorRecIdRef: null,
                PickupTrailerRecIdRef: null,
                DeliveryTrailerRecIdRef: null,
                Void: voidData.StatusRecIdRef === States.Voided.RecId,
                Cancel: voidData.StatusRecIdRef === States.Cancelled.RecId
              },
              where: { RecId: OrderRecIdRef }
            },
            refetchQueries: ["SINGLE_ORDER_QUERY", "ALL_ORDERS_QUERY"]
          });
          setModalOpen(false, "");
          toast.warning(
            `Order ${type === "Void" ? "voided" : "cancelled"} successfully.`
          );
        } catch (err) {
          toast.error(
            `Error ${type === "Void" ? "voiding" : "cancelling"} order`
          );
        }
      }}
    >
      <Title>Void / Cancel Order</Title>
      <Fieldset disabled={loading || readOnly}>
        <Column.Group style={{ marginBottom: 0 }}>
          <Column>
            <Field>
              <Control style={{ height: 51.5 }}>
                <Label>Type</Label>
                <Label disabled={readOnly}>
                  <Radio
                    defaultChecked={
                      inputs.StatusRecIdRef === States.Voided.RecId
                    }
                    disabled={[
                      States.Cancelled.RecId,
                      States.Voided.RecId
                    ].includes(inputs.StatusRecIdRef)}
                    name="type"
                    onChange={handleToggleType("void")}
                  />
                  &nbsp;Void
                </Label>
                <Label disabled={readOnly}>
                  <Radio
                    defaultChecked={
                      inputs.StatusRecIdRef === States.Cancelled.RecId
                    }
                    name="type"
                    onChange={handleToggleType("cancel")}
                  />
                  &nbsp;Cancel
                </Label>
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>{type} Reason</Label>
                <Input
                  autocomplete="new"
                  name="VoidReason"
                  type="text"
                  value={voidData?.VoidReason}
                  onChange={handleChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Notification Contact</Label>
                <Input
                  autocomplete="new"
                  name="VoidNotificationContact"
                  type="text"
                  value={voidData?.VoidNotificationContact}
                  onChange={handleChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Notice</Label>
                <Input
                  readOnly
                  name="Notice"
                  type="text"
                  value={`${round(notice, 2)} hrs`}
                />
              </Control>
            </Field>
          </Column>
          {/* COLUMN */}
          <Column>
            <Field>
              <Control>
                <Label>Delivery Date</Label>
                <DateInput
                  readOnly
                  name="ScheduledDeliveryDate"
                  value={voidData.ScheduledDeliveryDate}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>{type} Date</Label>
                <DateInput
                  name="VoidDate"
                  value={voidData?.VoidDate}
                  onChange={handleChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Notification Date</Label>
                <DateTime
                  name="VoidNotificationDate"
                  value={voidData?.VoidNotificationDate}
                  onChange={handleChange}
                />
              </Control>
            </Field>
            {voidData.VoidNotificationBy && (
              <Field>
                <Control>
                  <Label>{type === "Void" ? "Voided" : "Cancelled"} By</Label>
                  <Input
                    readOnly
                    type="text"
                    value={voidData.VoidNotificationBy}
                  />
                </Control>
              </Field>
            )}
          </Column>
        </Column.Group>

        <br />
      </Fieldset>
      <Field kind="group">
        <Control>
          <Button
            color="primary"
            disabled={readOnly}
            form="void-order-form"
            type="submit"
          >
            Save
          </Button>
        </Control>
        <Control>
          <Button onClick={() => setModalOpen(false, "")}>Cancel</Button>
        </Control>
      </Field>
    </form>
  );
};
VoidOrderForm.propTypes = {
  OrderRecIdRef: PropTypes.number.isRequired,
  inputs: PropTypes.object,
  setModalOpen: PropTypes.func
};
export default VoidOrderForm;
